import { FC, memo, ReactNode } from 'react';

import { FloatingTabs } from '@dbt-labs/sourdough';

import { useQueryParamOption } from '@/hooks/queryParamOption';
import { AnnotationBadge } from './AnnotationBadge';

const tabTypes = [
  'general',
  'code',
  'columns',
  'performance',
  'evaluator',
  'arguments',
  'dimensions',
  'measures',
  'queryExports',
  'tables',
  'views',
  'relationships',
] as const;
export type TabType = (typeof tabTypes)[number];

export type TabInfo = {
  type: TabType;
  count?: number;
};

const tabNameMap: Record<TabType, string> = {
  general: 'General',
  code: 'Code',
  columns: 'Columns',
  performance: 'Performance',
  evaluator: 'Recommendations',
  arguments: 'Arguments',
  dimensions: 'Dimensions',
  measures: 'Measures',
  queryExports: 'Query Exports',
  tables: 'Tables',
  views: 'Views',
  relationships: 'Relationships',
};

const tabAnnotationMap: Partial<Record<TabType, string>> = {};

const isTabType = (input: string | undefined | null): input is TabType => {
  return !!input && tabTypes.includes(input as TabType);
};

const getTabType = (input: string | undefined | null): TabType | null => {
  return isTabType(input) ? input : null;
};

type TabsParams = {
  tabs: TabInfo[];
  children: (tabType: TabType) => ReactNode;
  show: boolean;
};

const DetailTabs: FC<TabsParams> = ({ tabs, show, children }) => {
  const [tabParam, setTabParam] = useQueryParamOption({
    param: 'tab',
    options: tabTypes,
    validateOption: getTabType,
  });
  const activeTab = tabParam ? { type: tabParam } : tabs[0];

  const tabAnnotationMapFeatureFlagged = { ...tabAnnotationMap };
  return (
    <div
      className={`${show ? 'opacity-100' : 'opacity-0'} mt-10 duration-300 motion-reduce:duration-0`}
    >
      {tabs.length > 1 && (
        <div className="overflow-x-auto">
          <FloatingTabs testId="resource-view-tabs">
            {tabs.map((tabDetails) => (
              <FloatingTabs.Tab
                key={tabNameMap[tabDetails.type]}
                id={tabDetails.type}
                isActive={activeTab.type === tabDetails.type}
                onClick={(tab: TabType) => {
                  setTabParam(tab);
                }}
                count={tabDetails.count}
                testId={`resource-view-tabs-${tabDetails.type}`}
              >
                {tabNameMap[tabDetails.type]}
                {tabAnnotationMapFeatureFlagged[tabDetails.type] &&
                  tabDetails.count === undefined && (
                    <AnnotationBadge
                      type="text"
                      text={tabAnnotationMapFeatureFlagged[tabDetails.type]}
                    />
                  )}
              </FloatingTabs.Tab>
            ))}
          </FloatingTabs>
        </div>
      )}
      <>{activeTab.type && children(activeTab.type)}</>
    </div>
  );
};

export default memo(DetailTabs);
